import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

const Information = () => {
  const [open, setOpen] = useState(false);
  const infoRef = useRef(null);
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const infoContent = (
    <div style={{ lineHeight: '1.4' }}>
      <h3 style={{ fontSize: '1.1em', marginTop: '18px', marginBottom: '8px', fontWeight: 'bold' }}>Advisory</h3>
      <ul style={{ listStyle: 'disc', marginLeft: '15px', paddingLeft: '0' }}>
        <li>
          <strong>NoRisk:</strong> WHI = 0
        </li>
        <li>
          <strong>Low:</strong> WHI &lt; 50
        </li>
        <li>
          <strong>Medium:</strong> WHI 50 - 100
        </li>
        <li>
          <strong>High:</strong> WHI &ge; 100
        </li>
      </ul>

      <hr style={{ margin: '20px 0', borderColor: '#eee' }} />

      <h3 style={{ fontSize: '1.1em', marginTop: '18px', marginBottom: '8px', fontWeight: 'bold' }}>Weighted Hazard Index (WHI)</h3>
      <p style={{ marginTop: '0', marginBottom: '10px' }}>
        A weighted index based on the sum area percentage of hazard levels per suco.
      </p>
      
      <p style={{ fontWeight: 'bold', fontSize: '1em', marginBottom: '5px' }}>
        Formula:
      </p>
      <div style={{ background: '#f0f0f0', padding: '10px', borderRadius: '5px' }}>
        <code style={{ fontSize: '0.9em' }}>
          WHI = 0 * P<sub>no</sub> + 1 * P<sub>low</sub> + 2 * P<sub>med</sub> + 4 * P<sub>high</sub>
        </code>
      </div>
      
      <ul style={{ listStyle: 'disc', marginLeft: '25px', paddingLeft: '0', marginTop: '15px' }}>
        <li><strong>P<sub style={{fontWeight: 'normal'}}>no</sub>:</strong> Percentage of area with No Hazard</li>
        <li><strong>P<sub style={{fontWeight: 'normal'}}>low</sub>:</strong> Percentage of area with Low Hazard</li>
        <li><strong>P<sub style={{fontWeight: 'normal'}}>med</sub>:</strong> Percentage of area with Medium Hazard</li>
        <li><strong>P<sub style={{fontWeight: 'normal'}}>high</sub>:</strong> Percentage of area with High Hazard</li>
      </ul>
    </div>
  );

  return (
    <div style={{ position: "absolute", bottom: "20px", left: "12px", zIndex: 999 }}>
      {/* Info Button */}
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        title="More Info"
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px) brightness(1.1)",
          WebkitBackdropFilter: "blur(10px) brightness(1.1)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          padding: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 16,
          color: "black",
          transition: "transform 0.2s",
        }}
        aria-label="Show info"
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
      >
        <FaCircleInfo />
      </button>

      {/* Info Card */}
      {open && (
        <div
          ref={infoPanelRef}
          style={{
            position: "absolute",
            bottom: 30,
            left: 0,
            minWidth: 300,
            maxWidth: 450,
            maxHeight: 350,
            overflowY: 'auto',
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            padding: "16px 18px",
            fontSize: 13,
            color: "#333",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h5 style={{ fontWeight: 600, color: "#000" }}>Information</h5>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#888",
              }}
              aria-label="Close info"
            >
              ×
            </button>
          </div>
          <div>{infoContent}</div>
        </div>
      )}

      {/* Fade-in Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Information;